import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import {
    Button,
    TextField,
    MenuItem,
    FormControlLabel,
    Switch,
    Alert
} from "@mui/material"
import { ForumOutlined } from "@mui/icons-material"
import {
    useGetManageQuestionsQuery,
    useGetManageQuestionThreadQuery,
    useAnswerManageQuestionMutation,
    useDeleteManageQuestionMutation,
    useDeleteManageAnswerMutation
} from "../../Redux/actions/lecture.action"
import QuestionItem from "../components/QuestionItem"
import QuestionThreadDialog from "../components/QuestionThreadDialog"
import EmptyState from "../components/EmptyState"

const PAGE_SIZE = 20

/** One page of the inbox; pages share the RTK Query cache. */
function InboxPage({ filters, page, onOpen }) {
    const { data } = useGetManageQuestionsQuery({
        ...filters,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE
    })
    return _.get(data, "list", []).map((question) => (
        <QuestionItem
            key={question.question_id}
            question={question}
            context={_.compact([question.course_title, question.lecture_title]).join(
                " · "
            )}
            onOpen={onOpen}
        />
    ))
}

InboxPage.propTypes = {
    filters: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    onOpen: PropTypes.func.isRequired
}

/**
 * The owner's Q&A inbox: every student question across the platform's
 * courses, newest first. Filter to one course and / or to questions still
 * waiting for an instructor answer, open a thread to answer, and delete any
 * question or answer that should not be there.
 *
 * The course filter is controlled by the parent so a course's "Q&A" button
 * can open the inbox already filtered.
 */
export default function QuestionInbox({ courses, courseId, onCourseChange }) {
    const [unanswered, setUnanswered] = React.useState(true)
    const [pages, setPages] = React.useState(1)
    const [openId, setOpenId] = React.useState(null)

    const filters = React.useMemo(
        () => ({ course_id: courseId || undefined, unanswered }),
        [courseId, unanswered]
    )

    /* A new filter starts again from the first page. */
    React.useEffect(() => {
        setPages(1)
    }, [filters])

    const firstPage = useGetManageQuestionsQuery({
        ...filters,
        offset: 0,
        limit: PAGE_SIZE
    })
    const total = _.get(firstPage, "data.total", 0)

    const threadQuery = useGetManageQuestionThreadQuery(
        { question_id: openId },
        { skip: !openId }
    )
    const [answerAction] = useAnswerManageQuestionMutation()
    const [deleteQuestionAction] = useDeleteManageQuestionMutation()
    const [deleteAnswerAction] = useDeleteManageAnswerMutation()

    return (
        <div className="space-y-4">
            <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <TextField
                    select
                    size="small"
                    label="Course"
                    value={courseId || "all"}
                    onChange={(event) =>
                        onCourseChange(
                            event.target.value === "all" ? null : event.target.value
                        )
                    }
                    sx={{ minWidth: { xs: "100%", sm: 280 } }}
                >
                    <MenuItem value="all">All courses</MenuItem>
                    {courses.map((course) => (
                        <MenuItem key={course.course_id} value={course.course_id}>
                            {course.title}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControlLabel
                    control={
                        <Switch
                            checked={unanswered}
                            onChange={(event) => setUnanswered(event.target.checked)}
                        />
                    }
                    label={<span className="text-sm">Unanswered only</span>}
                />
            </section>

            {firstPage.isError && (
                <Alert severity="error">
                    {_.get(firstPage, "error.data.detail", null) ||
                        "Questions could not be loaded."}
                </Alert>
            )}

            {firstPage.isSuccess && total === 0 && (
                <EmptyState
                    icon={<ForumOutlined />}
                    title={unanswered ? "All caught up" : "No questions yet"}
                    description={
                        unanswered
                            ? "Every student question has an instructor answer."
                            : "Questions students ask under a lecture show up here."
                    }
                />
            )}

            {total > 0 && (
                <p className="text-xs text-slate-500">
                    {total} {total === 1 ? "question" : "questions"}
                </p>
            )}

            <div className="space-y-2">
                {_.range(pages).map((page) => (
                    <InboxPage
                        key={page}
                        filters={filters}
                        page={page}
                        onOpen={(question) => setOpenId(question.question_id)}
                    />
                ))}
            </div>

            {pages * PAGE_SIZE < total && (
                <div className="flex justify-center">
                    <Button size="small" onClick={() => setPages(pages + 1)}>
                        Load more questions
                    </Button>
                </div>
            )}

            <QuestionThreadDialog
                open={Boolean(openId)}
                thread={threadQuery.currentData}
                isLoading={threadQuery.isFetching && !threadQuery.currentData}
                error={
                    threadQuery.isError
                        ? _.get(threadQuery, "error.data.detail", null) ||
                          "This question could not be loaded."
                        : ""
                }
                replyLabel="Post instructor answer"
                canDelete={() => true}
                onClose={() => setOpenId(null)}
                onReply={(body) =>
                    answerAction({ question_id: openId, body }).unwrap()
                }
                onDeleteQuestion={async () => {
                    await deleteQuestionAction({ question_id: openId }).unwrap()
                    setOpenId(null)
                }}
                onDeleteAnswer={(answer) =>
                    deleteAnswerAction({ answer_id: answer.answer_id }).unwrap()
                }
            />
        </div>
    )
}

QuestionInbox.propTypes = {
    courses: PropTypes.array.isRequired,
    courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCourseChange: PropTypes.func.isRequired
}
